'use client';
import React, { useState } from 'react';
import { ChevronLeft, Eye, Save, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import Toggle from '@/components/ui/Toggle';
import { TutorConfig } from './AITutorBuilderContent';

interface Props {
  config: TutorConfig;
  updateConfig: (u: Partial<TutorConfig>) => void;
  onBack: () => void;
  onPreview: () => void;
  onSave: () => Promise<void>;
  saved: boolean;
}

const ageOptions = ['6-9', '10-13', '14-17', '18+'];

const contentBoundaryOptions = [
  { id: 'no-violence', label: 'No Violence', desc: 'Exclude violent or graphic content' },
  { id: 'no-adult-content', label: 'No Adult Content', desc: 'Block all adult or sexual content' },
  { id: 'no-politics', label: 'No Political Content', desc: 'Avoid partisan political discussions' },
  { id: 'no-religion', label: 'No Religious Debate', desc: 'Skip comparative religious argumentation' },
  { id: 'safe-search', label: 'Safe Search Only', desc: 'Only reference verified educational sources' },
];

export default function TutorBuilderStep4({ config, updateConfig, onBack, onPreview, onSave, saved }: Props) {
  const [saving, setSaving] = useState(false);

  const toggleBoundary = (id: string) => {
    const updated = config.contentBoundaries.includes(id)
      ? config.contentBoundaries.filter((b) => b !== id)
      : [...config.contentBoundaries, id];
    updateConfig({ contentBoundaries: updated });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Safety & Finalize</h2>
        <p className="text-sm text-muted-foreground">Set age restrictions, content boundaries, and save your tutor to your dashboard.</p>
      </div>

      {/* Age restriction */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Age Restriction</label>
        <p className="text-xs text-muted-foreground mb-3">Which age group is this tutor designed for? Lessons and language will adapt accordingly.</p>
        <div className="flex flex-wrap gap-2">
          {ageOptions.map((age) => (
            <button
              key={`age-opt-${age}`}
              type="button"
              onClick={() => updateConfig({ ageRestriction: age })}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all btn-scale ${
                config.ageRestriction === age
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              Ages {age}
            </button>
          ))}
        </div>
      </div>

      {/* Content boundaries */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Content Boundaries</label>
        <p className="text-xs text-muted-foreground mb-3">Select which content types the tutor should avoid. These are enforced automatically.</p>
        <div className="space-y-2">
          {contentBoundaryOptions.map((b) => {
            const active = config.contentBoundaries.includes(b.id);
            return (
              <div
                key={`boundary-${b.id}`}
                className={`flex items-center justify-between p-3 rounded-xl border transition-all ${active ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}
              >
                <div>
                  <p className="text-sm font-semibold text-foreground">{b.label}</p>
                  <p className="text-xs text-muted-foreground">{b.desc}</p>
                </div>
                <Toggle checked={active} onChange={() => toggleBoundary(b.id)} size="sm" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Parent approval */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-amber-600" />
          <p className="text-sm font-bold text-amber-800">Parental Oversight Settings</p>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Require Parent Approval</p>
            <p className="text-xs text-muted-foreground">Parent must approve before tutor activates for this student</p>
          </div>
          <Toggle
            checked={config.parentApprovalRequired}
            onChange={(v) => updateConfig({ parentApprovalRequired: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Enable Safe Mode</p>
            <p className="text-xs text-muted-foreground">Extra filtering layer — all responses reviewed against safety rules</p>
          </div>
          <Toggle
            checked={config.safeMode}
            onChange={(v) => updateConfig({ safeMode: v })}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-muted/50 rounded-xl border border-border">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Tutor Summary</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Name:</span> <span className="font-semibold text-foreground">{config.name || '—'}</span></div>
          <div><span className="text-muted-foreground">Avatar:</span> <span>{config.avatar}</span></div>
          <div><span className="text-muted-foreground">Personality:</span> <span className="font-semibold text-foreground capitalize">{config.personality}</span></div>
          <div><span className="text-muted-foreground">Tone:</span> <span className="font-semibold text-foreground capitalize">{config.tone}</span></div>
          <div><span className="text-muted-foreground">Courses:</span> <span className="font-semibold text-foreground">{config.courses.length} selected</span></div>
          <div><span className="text-muted-foreground">Difficulty:</span> <span className="font-semibold text-foreground capitalize">{config.difficulty}</span></div>
          <div><span className="text-muted-foreground">Memory:</span> <span className="font-semibold text-foreground">{config.memoryEnabled ? 'Enabled' : 'Disabled'}</span></div>
          <div><span className="text-muted-foreground">Safe Mode:</span> <span className="font-semibold text-foreground">{config.safeMode ? 'On' : 'Off'}</span></div>
        </div>
      </div>

      {saved && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl fade-in">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-700">Tutor saved to your dashboard! Start a session anytime.</p>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={onPreview}
          className="flex items-center gap-2 px-5 py-2.5 border border-primary/30 bg-primary/5 text-primary rounded-xl text-sm font-semibold btn-scale hover:bg-primary/10 transition-colors"
        >
          <Eye size={16} /> Preview Tutor
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || saved || !config.name}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Tutor to Dashboard'}
        </button>
      </div>
    </div>
  );
}